import {
  ChartBarIcon,
  CreditCardIcon,
  UserAddIcon,
  UsersIcon,
} from '@heroicons/react/outline';
import { useRouter } from 'next/router';
import type { ComponentType, SVGProps } from 'react';

import clsxm from '@/lib/clsxm';

import { SectionHeading, Stage, tone } from '@/components/agents/primitives';

import { Avatar, COMPANY, TEAM, type Teammate, useB2bTranslation } from './kit';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

const MEMBERS: {
  person: Teammate;
  owner?: boolean;
  words: number;
  credits: number;
}[] = [
  { person: TEAM.maya, owner: true, words: 48210, credits: 186 },
  { person: TEAM.daniel, words: 31450, credits: 42 },
  { person: TEAM.sofia, words: 22900, credits: 12 },
  { person: TEAM.arjun, words: 8320, credits: 64 },
  { person: TEAM.lena, words: 1240, credits: 0 },
];

const WORDS_ALLOWANCE = 100000;

export default function TeamsAdmin() {
  const { t } = useB2bTranslation();

  const points: { icon: Icon; title: string; detail: string }[] = [
    {
      icon: UserAddIcon,
      title: t('Invite by email or link'),
      detail: t('Teammates join your workspace in a minute.'),
    },
    {
      icon: UsersIcon,
      title: t('Add seats as you grow'),
      detail: t('Add or remove seats from your team settings at any time.'),
    },
    {
      icon: ChartBarIcon,
      title: t('Usage for every person'),
      detail: t('See the words and credits each teammate used this month.'),
    },
    {
      icon: CreditCardIcon,
      title: t('One bill for everyone'),
      detail: t('Top up one person’s credits without changing the whole plan.'),
    },
  ];

  return (
    <section id='admin' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('You stay in control.')}
          tail={t('Without chasing anyone.')}
          lead={t(
            'Invite people, manage seats and see how each person uses AI, all from one page.',
          )}
        />

        <div className='mt-14 grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-12'>
          <ul className='grid min-w-0 gap-8 sm:grid-cols-2 lg:col-span-4 lg:grid-cols-1'>
            {points.map(({ icon: PointIcon, title, detail }) => (
              <li key={title} className='flex gap-4'>
                <span className='flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white text-primary-600 ring-1 ring-[#0f0f28]/[0.07] dark:bg-white/[0.05] dark:text-primary-300 dark:ring-white/10'>
                  <PointIcon className='h-5 w-5' />
                </span>
                <div>
                  <h3 className={clsxm('text-base font-semibold', tone.ink)}>
                    {title}
                  </h3>
                  <p className={clsxm('mt-1 text-[15px] leading-7', tone.body)}>
                    {detail}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div className='min-w-0 lg:col-span-8'>
            <Stage
              variant='lavender'
              halftoneAt='100% 0%'
              className='-mx-3 p-3 sm:mx-0 sm:p-8'
            >
              <div aria-hidden='true'>
                <div>
                  <TeamPage />
                </div>
              </div>
            </Stage>
          </div>
        </div>
      </div>
    </section>
  );
}

function TeamPage() {
  const { t } = useB2bTranslation();
  const { locale } = useRouter();
  const numberLocale = locale === 'ar' ? 'ar-u-nu-latn' : locale;
  const number = (value: number) =>
    new Intl.NumberFormat(numberLocale).format(value);

  return (
    <div className='overflow-hidden rounded-[22px] bg-white text-start shadow-[0_40px_90px_-40px_rgba(22,12,90,0.55)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
      <div className='flex flex-wrap items-center justify-between gap-4 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
        <div>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
            {t('Team')}
          </p>
          <p className={clsxm('text-xs', tone.quiet)}>
            {COMPANY} · {t('Teams 10 seats')}
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <div className='w-32 sm:w-40'>
            <div className='flex items-center justify-between text-[11px] font-medium'>
              <span className={tone.quiet}>{t('Seats')}</span>
              <span className={clsxm('tabular-nums', tone.ink)} dir='ltr'>
                7 / 10
              </span>
            </div>
            <div className='mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#ececf3] dark:bg-white/10'>
              <div className='h-full w-[70%] rounded-full bg-primary-500' />
            </div>
          </div>
          <span className='rounded-full bg-[#10101c] px-3 py-1.5 text-xs font-semibold text-white dark:bg-white dark:text-[#10101c]'>
            {t('Add seats')}
          </span>
        </div>
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full text-start text-[13px] sm:min-w-[34rem]'>
          <thead>
            <tr className={clsxm('text-xs', tone.quiet)}>
              <th className='px-5 pb-2 pt-4 text-start font-medium'>
                {t('Member')}
              </th>
              <th className='px-3 pb-2 pt-4 text-start font-medium'>
                {t('Words used')}
              </th>
              <th className='hidden px-3 pb-2 pt-4 text-end font-medium sm:table-cell'>
                {t('Image and video credits')}
              </th>
              <th className='hidden w-px px-5 pb-2 pt-4 sm:table-cell' />
            </tr>
          </thead>
          <tbody className='divide-y divide-black/[0.05] dark:divide-white/[0.06]'>
            {MEMBERS.map(({ person, owner, words, credits }) => (
              <tr key={person.name}>
                <td className='px-5 py-3'>
                  <span className='flex items-center gap-3'>
                    <Avatar person={person} />
                    <span className='min-w-0'>
                      <span
                        className={clsxm(
                          'block truncate font-semibold',
                          tone.ink,
                        )}
                      >
                        {person.name}
                      </span>
                      <span className={clsxm('block text-xs', tone.quiet)}>
                        {owner ? t('Owner') : t('Member')}
                      </span>
                    </span>
                  </span>
                </td>
                <td className='px-3 py-3'>
                  <span className='flex items-center gap-2.5'>
                    <span className='h-1.5 w-16 flex-none overflow-hidden rounded-full bg-[#ececf3] sm:w-24 dark:bg-white/10'>
                      <span
                        className='block h-full rounded-full bg-primary-400'
                        style={{ width: `${(words / WORDS_ALLOWANCE) * 100}%` }}
                      />
                    </span>
                    <span className={clsxm('tabular-nums', tone.body)}>
                      {number(words)}
                    </span>
                  </span>
                </td>
                <td
                  className={clsxm(
                    'hidden px-3 py-3 text-end tabular-nums sm:table-cell',
                    tone.body,
                  )}
                >
                  {number(credits)}
                </td>
                <td className='hidden px-5 py-3 text-end sm:table-cell'>
                  {person === TEAM.arjun ? (
                    <span className='whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold text-primary-700 ring-1 ring-primary-200 dark:text-primary-300 dark:ring-primary-400/30'>
                      {t('Buy credits')}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-black/[0.06] bg-[#fafafc] px-5 py-4 dark:border-white/[0.06] dark:bg-white/[0.02]'>
        <p className={clsxm('text-[13px] font-semibold', tone.ink)}>
          {t('Invite to {{company}}', { company: COMPANY })}
        </p>
        <div className='flex min-w-[14rem] flex-1 items-center gap-2 rounded-xl bg-white px-3 py-1.5 ring-1 ring-black/[0.07] dark:bg-white/[0.05] dark:ring-white/10'>
          <span
            className={clsxm('min-w-0 flex-1 truncate text-[13px]', tone.ink)}
            dir='ltr'
          >
            tom@kalis.co
          </span>
          <span className='flex-none rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white'>
            {t('Invite')}
          </span>
        </div>
        <p className={clsxm('flex items-center gap-1.5 text-xs', tone.quiet)}>
          <LinkGlyph className='h-3.5 w-3.5 flex-none' />
          {t('Or copy your invite link')}
        </p>
      </div>
    </div>
  );
}

function LinkGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='currentColor'
      aria-hidden='true'
      className={className}
    >
      <path d='M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.142-3.667l3-3z' />
      <path d='M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.142 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 105.656 5.656l3-3a4 4 0 00-.225-5.865z' />
    </svg>
  );
}
